"use client";

import { Time, type parseDuration } from "@internationalized/date";
import { useLocale, useTranslations } from "next-intl";
import Image from "next/image";
import { Fragment, use, type ReactNode, type SVGAttributes } from "react";

import { useTimeFormatter } from "@trip/react-hooks";

import { CheckmarkIcon } from "~/components/icons/checkmark-icon";
import { ClockIcon } from "~/components/icons/clock-icon";
import { CurrencyIcon } from "~/components/icons/currency-icon";
import { DurationIcon } from "~/components/icons/duration-icon";
import { FlagIcon } from "~/components/icons/flag-icon";
import { QuestionMarkIcon } from "~/components/icons/question-mark";
import { LoadingSpinner } from "~/components/loading-spinner";
import { Prose } from "~/components/prose";
import { StructuredText } from "~/components/structured-text";
import { H2 } from "~/components/typography";
import { graphql } from "~/lib/dato-graphql";
import { getDatoLocale } from "~/lib/dato-utils";
import { useDatoQuery } from "~/lib/use-urql-queries";

import type { Transfer } from "../_lib/transfer";
import { TripContext } from "../_lib/trip-context";
import { LearnMore } from "./learn-more";

const TRIP_TRANSFER_INFO_QUERY = graphql(`
  query TripTransferInfo($locale: SiteLocale!) {
    tripTransferArticle(locale: $locale, fallbackLocales: en) {
      content {
        blocks
        links
        value
      }
    }
  }
`);

function formatDuration(duration: ReturnType<typeof parseDuration>) {
  const parts = [];

  if (duration.years) {
    parts.push(duration.years + "y");
  }
  if (duration.months) {
    parts.push(duration.months + "m");
  }
  if (duration.weeks) {
    parts.push(duration.weeks + "w");
  }
  if (duration.days) {
    parts.push(duration.days + "d");
  }
  if (duration.hours) {
    parts.push(duration.hours + "h");
  }
  if (duration.minutes) {
    parts.push(duration.minutes + "m");
  }
  if (duration.seconds) {
    parts.push(duration.seconds + "s");
  }

  return parts.join(" ");
}

export function Inbound({
  distance,
  duration,
  suggestedDeparture: [suggestedDepartureFrom, suggestedDepartureTo],
}: Transfer) {
  const t = useTranslations("TripDetails");
  const tTour = useTranslations("TourDetails");
  const locale = useLocale();
  const trip = use(TripContext);
  if (!trip) {
    throw new Error("TripContext is not available");
  }
  const [{ data, fetching: isLoading }] = useDatoQuery({
    query: TRIP_TRANSFER_INFO_QUERY,
    variables: { locale: getDatoLocale(locale) },
  });
  const formatTime = useTimeFormatter();
  function cell(
    Icon: (props: SVGAttributes<SVGSVGElement>) => ReactNode,
    label: ReactNode,
    value: ReactNode,
  ) {
    return (
      <Fragment>
        <div className="flex items-center gap-2 text-neutral-500">
          <Icon className="size-6" />
          <div>{label}</div>
        </div>
        <div>{value}</div>
      </Fragment>
    );
  }
  return (
    <div className="mt-4 grid grid-cols-2 gap-4 md:w-1/2">
      {cell(
        ClockIcon,
        t("suggestedDeparture"),
        <>
          {t("departureTimeBetween", {
            fromTime: formatTime(new Time(suggestedDepartureFrom, 0)),
            toTime: formatTime(new Time(suggestedDepartureTo, 0)),
          })}
        </>,
      )}
      {cell(DurationIcon, tTour("duration"), <>{formatDuration(duration)}</>)}
      {cell(FlagIcon, t("distance"), <>{t("distanceKm", { distance })}</>)}
      {cell(
        CurrencyIcon,
        t("tickets"),
        trip.transfersIncluded ? (
          <div className="flex items-center gap-2">
            {t("japanRailPassIncluded")}
            <CheckmarkIcon className="text-green size-6" />
          </div>
        ) : (
          <>{t("notIncluded")}</>
        ),
      )}
      {trip.transfersIncluded
        ? cell(
            QuestionMarkIcon,
            t("information"),
            <LearnMore label={t("learnMore")} type="transfers">
              <div className="mx-auto my-8 max-w-4xl">
                <H2 className="mb-4">{t("transfers")}</H2>
                <div className="mb-4">
                  {isLoading || !data?.tripTransferArticle ? (
                    <LoadingSpinner />
                  ) : (
                    <Prose>
                      <StructuredText data={data.tripTransferArticle.content} />
                    </Prose>
                  )}
                </div>
                <div className="flex w-full grid-cols-[1.5fr_1.5fr_0.6fr] flex-col gap-2 *:w-full sm:grid sm:flex-row sm:gap-6 sm:*:h-full sm:*:object-cover [&>img]:rounded-lg sm:[&>img]:rounded-2xl">
                  <Image
                    alt="Train"
                    src="/transfers/AdobeStock_192107646.jpeg"
                    width={5885}
                    height={3923}
                  />
                  <Image
                    alt="Train"
                    src="/transfers/AdobeStock_602779466.jpeg"
                    width={6048}
                    height={4032}
                  />
                  <Image
                    alt="Train"
                    src="/transfers/scmaglev-railway-park.jpg"
                    width={1960}
                    height={3268}
                  />
                </div>
              </div>
            </LearnMore>,
          )
        : null}
    </div>
  );
}
